import React, { useCallback, useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { ArtifactDetail, ArtifactsSidebar, ArtifactsSlider } from '@pkg/components';
import { useAppSelector } from '@pkg/hooks';

import { ArtifactType } from '@app/client/common/structs/nft/artifacts';
import { PathParams } from '@app/routes/types';
import { userArtifacts } from '@app/store/selectors/artifacts/artifacts';
import { artifactDisplayMetadata } from '@app/store/selectors/artifacts/artifacts-metadata';
import { artifactKeyToUrlMap, artifactUrlToKeyMap } from '@app/store/types/artifacts';

import './ArtifactsList.scss';

type ArtifactsListProps = {
    expanded: boolean;
    muted: boolean;
};

export function ArtifactsList({ expanded, muted }: ArtifactsListProps) {
    const navigate = useNavigate();

    const { [PathParams.ArtifactType]: urlArtifactType } = useParams();

    const { totalKeys } = useAppSelector(userArtifacts);
    const artifactTypeDisplayItems = useAppSelector(artifactDisplayMetadata);
    const firstType = Array.from(Object.keys(ArtifactType))[0] as ArtifactType;
    const artifactType = artifactUrlToKeyMap.get(urlArtifactType as string) || firstType;

    const initialRoseIndex = totalKeys.indexOf(artifactType);
    const [activeIndex, setActiveIndex] = useState(initialRoseIndex);

    useEffect(() => {
        const newArtifactType = artifactUrlToKeyMap.get(urlArtifactType as string) || firstType;
        const newIndex = totalKeys.indexOf(newArtifactType);
        setActiveIndex(newIndex);
    }, [totalKeys, urlArtifactType, firstType]);

    const goToArtifactAtIndex = useCallback(
        (index) => {
            const currentArtifactType = totalKeys[index];
            if (currentArtifactType) {
                const currentArtifactTypeUrlPath = artifactKeyToUrlMap.get(currentArtifactType);
                navigate(`/artifacts/${currentArtifactTypeUrlPath}`);
            }
        },
        [navigate, totalKeys],
    );

    const handleSlideChange = useCallback(
        (currentIndex) => {
            goToArtifactAtIndex(currentIndex);
        },
        [goToArtifactAtIndex],
    );

    return (
        <div className="yat-artifacts__list">
            <ArtifactsSidebar />
            <ArtifactsSlider activeIndex={activeIndex} expanded={expanded} onChange={handleSlideChange}>
                {totalKeys.map((currentArtifactType) => {
                    const isActive = artifactType === currentArtifactType;
                    return (
                        <ArtifactDetail
                            key={currentArtifactType}
                            artifactType={currentArtifactType}
                            active={isActive}
                            expanded={expanded}
                            muted={muted}
                            videoUrl={artifactTypeDisplayItems[currentArtifactType]?.animation_url}
                            posterUrl={artifactTypeDisplayItems[currentArtifactType]?.thumbnail_image}
                        />
                    );
                })}
            </ArtifactsSlider>
        </div>
    );
}
